/**
 * Competition Plugin Service
 *
 * Service layer for managing competitions based on registered discipline plugins.
 * Synchronizes frontend with backend plugin configurations and provides
 * type-specific competition operations.
 */

import { API_CONFIG } from '../config/api';

// =============================================================================
// TYPES - Aligned with backend plugin responses
// =============================================================================

export interface CompetitionTypeFeatures {
  // Bridge-specific
  barometer_scoring?: boolean;
  web_results?: boolean;
  butler_scoring?: boolean;
  complete_comparison?: boolean;
  partner_rotation?: boolean;
  seeded_draw?: boolean;
  carryover?: boolean;
  swiss_style?: boolean;
  complete_rounds?: boolean;
  swiss_pairing?: boolean;
  multi_venue?: boolean;
  common_boards?: boolean;
  centralized_scoring?: boolean;
  simplified_rules?: boolean;
  teaching_mode?: boolean;

  // Calcio Balilla-specific
  elimination_type?: 'single' | 'double';
  consolation_bracket?: boolean;
  third_place_match?: boolean;
  losers_bracket?: boolean;
  round_robin?: boolean;
  playoffs?: boolean;
  buchholz_tiebreaker?: boolean;
  flexible_format?: boolean;
  substitutions_allowed?: boolean;
  match_format?: string;
  draw_partner?: boolean;
  random_pairing?: boolean;
  skill_pairing?: boolean;
  handicap_system?: boolean;

  // Campionato a Giornate specific
  sets_per_match?: number;
  sets_per_match_options?: number[];
  sets_per_match_configurable?: boolean;
  score_per_set?: number;
  score_per_set_options?: number[];
  score_per_set_configurable?: boolean;
  matches_per_team_per_day?: number;

  // Generic
  [key: string]: unknown;
}

export interface BackendCompetitionType {
  code: string;
  name: string;
  description: string;
  participant_type: 'individual' | 'pair' | 'team';
  min_participants: number;
  max_participants: number | null;
  recommended_participants: number | null;
  scoring_systems: string[];
  default_scoring: string;
  status: 'available' | 'beta' | 'planned' | 'deprecated';
  features: CompetitionTypeFeatures;
  boards_per_round?: number;
  default_round_duration_minutes?: number | null;
}

/**
 * Movement type as served by the backend plugin (get_movement_types()).
 * BRIDGE: mitchell_standard, mitchell_skip, mitchell_relay, howell_complete,
 *         howell_partial, web_mitchell. Other disciplines: [].
 */
export interface BackendMovementType {
  code: string;
  name: string;
  description: string;
  applicable_competition_types: string[];
  min_tables: number;
  max_tables: number | null;
  supports_odd: boolean;
  is_default: boolean;
  icon?: string | null;
  features: Record<string, unknown>;
}

/** Tipo di composizione partecipante ('tipo di squadra') dal backend. */
export interface BackendSquadType {
  code: string;
  name: string;
  description?: string;
  participant_type: 'individual' | 'pair' | 'team';
  composed_of: 'individual' | 'pair' | 'team';
  players_per_unit: number;
  min_units: number;
  max_units: number | null;
  min_players: number | null;
  max_players: number | null;
  is_default: boolean;
  icon?: string | null;
  /** Etichetta singolare dell'unità partecipante (es. "Coppia", "Squadra", "Giocatore"). */
  unit_label_singular?: string | null;
  /** Etichetta plurale dell'unità partecipante (es. "Coppie", "Squadre", "Giocatori"). */
  unit_label_plural?: string | null;
}

export interface BackendStaffRole {
  role_code: string;
  role_name: string;
  role_name_plural: string;
  required_for_competition: boolean;
  min_required: number;
  max_allowed?: number;
  description: string;
  hierarchy_levels: string[];
  can_be_player?: boolean;
  icon?: string;
}

export interface BackendPlayerCategory {
  category_code: string;
  category_name: string;
  description: string;
  can_earn_points: boolean;
  can_participate_official: boolean;
  age_min?: number;
  age_max?: number;
  entry_fee_multiplier?: string;
  icon?: string;
}

export interface BackendCompetitionLevel {
  level: string;
  name: string;
  description: string;
  min_arbitro_level: string | null;
  min_participants: number;
  awards_ranking_points: boolean;
  ranking_multiplier: string;
  requires_homologation: boolean;
  icon?: string;
}

export interface BackendScoringSystem {
  code: string;
  name: string;
  description: string;
  scoring_type: string;  // points, percentage, imp, elo, sets, etc.
  is_higher_better: boolean;
  min_score: string | null;  // String from backend DTO
  max_score: string | null;  // String from backend DTO
  decimal_places: number;
  format_template: string;
  icon: string | null;
  display_order: number;
}

export interface BackendRankingSystem {
  code: string;
  name: string;
  description: string;
  method: string;  // points, elo, etc.
  points_per_win: string;  // String from backend DTO
  points_per_draw: string;  // String from backend DTO
  points_per_loss: string;  // String from backend DTO
  initial_rating: string;  // String from backend DTO
  k_factor: string;  // String from backend DTO
  min_games_for_ranking: number;
  icon?: string | null;
}

export interface BackendPairingStrategy {
  code: string;
  name: string;
  description: string;
  first_day_strategy: string;
  subsequent_days_strategy: string;
  use_historical_ranking: boolean;
  skill_balance_tolerance: string;  // String from backend DTO
  avoid_repeat_pairings: boolean;
  repeat_pairing_cooldown_days: number;
  icon?: string | null;
}

export interface BackendCompetitionFormat {
  code: string;
  name: string;
  description: string;
  format_type: string;
  has_seasons: boolean;
  dynamic_pairing: boolean;
  min_days_per_season: number;
  max_days_per_season: number | null;
  ranking_carryover: boolean;
  carryover_percentage: string;  // String from backend DTO
  icon?: string | null;
}

export interface BackendDisciplineTerminology {
  competition_singular: string;
  competition_plural: string;
  participant_singular: string;
  participant_plural: string;
  match_singular: string;
  match_plural: string;
  result_label: string;
}

export interface BackendDisciplineBookingTerminology {
  section_title: string;
  total_label: string;
  confirmed_label: string;
  pending_label: string;
  single_available_label: string;
  info_title: string;
  open_label: string;
  close_label: string;
  notes_label: string;
}

export interface BackendDisciplineNavigationCard {
  id: string;
  icon: string;
  label: string;
  description: string;
  href: string;
  visible_to: 'all' | 'organizer' | 'finance';
  order: number;
}

export interface BackendDisciplineSections {
  show_booking_inline?: boolean;
  show_staff?: boolean;
  show_managers?: boolean;
  show_prizes?: boolean;
  show_participation_fees?: boolean;
}

export interface BackendDisciplineUIConfig {
  // UI presentation (used by the frontend adapter)
  color?: string;
  discipline_icon?: string;
  terminology?: BackendDisciplineTerminology;
  booking_terminology?: BackendDisciplineBookingTerminology;
  navigation_cards?: BackendDisciplineNavigationCard[];
  sections?: BackendDisciplineSections;
  // Behavioural flags (existing, consumed elsewhere)
  supports_directional_rankings?: boolean;
  directional_labels?: Record<string, string> | null;
  supports_match_entry?: boolean;
  supports_aggregate_entry?: boolean;
  is_physical_sport?: boolean;
  is_mind_sport?: boolean;
  quick_guide_steps?: Array<{ title: string; description: string }>;
}

export interface BackendSportDiscipline {
  id: string;
  name: string;
  code: string;
  description: string;
  is_active: boolean;
  icon: string;
  display_order: number;
  has_implementation: boolean;
  competition_types_count: number;
  implementation_status: string | null;
  implementation_version: string | null;
  competition_types: BackendCompetitionType[];
  squad_types?: BackendSquadType[];
  movement_types?: BackendMovementType[];  // From plugin get_movement_types(). Bridge: 6 items; others: [].
  staff_roles: BackendStaffRole[];
  membership_categories: BackendPlayerCategory[];
  competition_levels: BackendCompetitionLevel[];
  scoring_systems?: BackendScoringSystem[];  // From plugin get_scoring_systems()
  ranking_systems?: BackendRankingSystem[];  // From plugin get_ranking_systems()
  pairing_strategies?: BackendPairingStrategy[];  // From plugin get_pairing_strategies()
  competition_formats?: BackendCompetitionFormat[];  // From plugin get_competition_formats()
  ui_config?: BackendDisciplineUIConfig;  // From plugin get_discipline_ui_config()
  // Core/plugin compatibility fields (added by BE versioning system)
  core_version?: string | null;
  plugin_min_core?: string | null;
  plugin_max_core?: string | null;
  plugin_db_schema_version?: string | null;
  core_compatible?: boolean | null;
  core_compat_reason?: string | null;
  // «Ha un gioco al tavolo con il link personale», derivato dal BE dai
  // router del plugin (§5.7/T-108 di funzionalita-nel-plugin.md).
  play_surface?: boolean;
  play_surface_label?: string | null;
}

export interface CompetitionManagementConfig {
  // Competition lifecycle operations available
  canStart: boolean;
  canPause: boolean;
  canResume: boolean;
  canFinish: boolean;
  canCancel: boolean;

  // Participant management
  canAddParticipants: boolean;
  canRemoveParticipants: boolean;
  canModifyPairs: boolean;
  canModifyTeams: boolean;

  // Round/Match management
  hasRounds: boolean;
  canGenerateRounds: boolean;
  canManualPairing: boolean;
  canEnterScores: boolean;
  canEditScores: boolean;

  // Results
  hasStandings: boolean;
  hasDetailedResults: boolean;
  canExportResults: boolean;

  // Special features
  features: CompetitionTypeFeatures;
}

// =============================================================================
// SERVICE
// =============================================================================

class CompetitionPluginService {
  private disciplinesCache: Map<string, BackendSportDiscipline> = new Map();
  private competitionTypesCache: Map<string, BackendCompetitionType> = new Map();
  private lastFetch: number = 0;
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  /**
   * Fetch all disciplines with their competition types from backend
   */
  async fetchDisciplines(forceRefresh = false): Promise<BackendSportDiscipline[]> {
    const now = Date.now();

    if (!forceRefresh && this.disciplinesCache.size > 0 && (now - this.lastFetch) < this.CACHE_TTL) {
      return Array.from(this.disciplinesCache.values());
    }

    try {
      // Use relative URL to go through Next.js proxy (works when accessed from other devices)
      const response = await fetch(
        `/api/sport-disciplines/with-implementation?is_active=true&limit=500`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch disciplines: ${response.status}`);
      }

      const disciplines: BackendSportDiscipline[] = await response.json();

      // Update caches
      this.disciplinesCache.clear();
      this.competitionTypesCache.clear();

      for (const discipline of disciplines) {
        this.disciplinesCache.set(discipline.code, discipline);

        for (const competitionType of discipline.competition_types) {
          const key = `${discipline.code}:${competitionType.code}`;
          this.competitionTypesCache.set(key, competitionType);
        }
      }

      this.lastFetch = now;
      return disciplines;
    } catch (error) {
      console.error('Error fetching disciplines:', error);
      throw error;
    }
  }

  /**
   * Get a specific discipline by code
   */
  async getDiscipline(code: string): Promise<BackendSportDiscipline | undefined> {
    if (this.disciplinesCache.has(code)) {
      return this.disciplinesCache.get(code);
    }

    await this.fetchDisciplines();
    return this.disciplinesCache.get(code);
  }

  /**
   * Get a specific competition type
   */
  async getCompetitionType(
    disciplineCode: string,
    competitionTypeCode: string
  ): Promise<BackendCompetitionType | undefined> {
    const key = `${disciplineCode}:${competitionTypeCode}`;

    if (this.competitionTypesCache.has(key)) {
      return this.competitionTypesCache.get(key);
    }

    await this.fetchDisciplines();
    return this.competitionTypesCache.get(key);
  }

  /**
   * Get competition types for a discipline
   */
  async getCompetitionTypesForDiscipline(disciplineCode: string): Promise<BackendCompetitionType[]> {
    const discipline = await this.getDiscipline(disciplineCode);
    return discipline?.competition_types || [];
  }

  /**
   * Get management configuration for a competition type
   * This determines what operations are available based on the competition type
   */
  getManagementConfig(competitionType: BackendCompetitionType): CompetitionManagementConfig {
    const features = competitionType.features;
    const participantType = competitionType.participant_type;

    // Base configuration
    const config: CompetitionManagementConfig = {
      // Lifecycle
      canStart: true,
      canPause: true,
      canResume: true,
      canFinish: true,
      canCancel: true,

      // Participants
      canAddParticipants: true,
      canRemoveParticipants: true,
      canModifyPairs: participantType === 'pair',
      canModifyTeams: participantType === 'team',

      // Rounds
      hasRounds: true,
      canGenerateRounds: true,
      canManualPairing: !features.swiss_pairing && !features.draw_partner,
      canEnterScores: true,
      canEditScores: true,

      // Results
      hasStandings: true,
      hasDetailedResults: true,
      canExportResults: true,

      // Features
      features
    };

    // Adjust based on specific features
    if (features.swiss_pairing) {
      config.canManualPairing = false;
    }

    if (features.draw_partner || features.random_pairing) {
      config.canModifyPairs = false;
    }

    if (features.elimination_type === 'single' || features.elimination_type === 'double') {
      config.hasRounds = true;
      config.canGenerateRounds = true;
    }

    if (features.round_robin) {
      config.hasRounds = true;
      config.canGenerateRounds = true;
    }

    return config;
  }

  /**
   * Get UI component configuration for a competition type
   * Returns which components should be rendered
   */
  getUIComponentsConfig(competitionType: BackendCompetitionType): {
    showBoardsConfig: boolean;
    showRoundsConfig: boolean;
    showBarometerToggle: boolean;
    showHandicapConfig: boolean;
    showTeamSizeConfig: boolean;
    showEliminationConfig: boolean;
    showSwissConfig: boolean;
    showMatchFormatConfig: boolean;
    participantLabel: string;
    participantLabelPlural: string;
  } {
    const features = competitionType.features;
    const participantType = competitionType.participant_type;

    const participantLabels: Record<string, { singular: string; plural: string }> = {
      individual: { singular: 'Giocatore', plural: 'Giocatori' },
      pair: { singular: 'Coppia', plural: 'Coppie' },
      team: { singular: 'Squadra', plural: 'Squadre' }
    };

    return {
      showBoardsConfig: !!competitionType.boards_per_round,
      showRoundsConfig: !!features.swiss_pairing || !!features.round_robin,
      showBarometerToggle: !!features.barometer_scoring,
      showHandicapConfig: !!features.handicap_system,
      showTeamSizeConfig: participantType === 'team',
      showEliminationConfig: !!features.elimination_type,
      showSwissConfig: !!features.swiss_pairing,
      showMatchFormatConfig: !!features.match_format,
      participantLabel: participantLabels[participantType]?.singular || 'Partecipante',
      participantLabelPlural: participantLabels[participantType]?.plural || 'Partecipanti'
    };
  }

  /**
   * Validate competition configuration against type constraints
   */
  validateCompetitionConfig(
    competitionType: BackendCompetitionType,
    config: {
      participants?: number;
      scoringSystem?: string;
      boardsPerRound?: number;
      rounds?: number;
    }
  ): { valid: boolean; errors: string[]; warnings: string[] } {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Validate participant count
    if (config.participants !== undefined) {
      if (config.participants < competitionType.min_participants) {
        errors.push(
          `Minimo ${competitionType.min_participants} ${
            competitionType.participant_type === 'pair' ? 'coppie' :
            competitionType.participant_type === 'team' ? 'squadre' : 'giocatori'
          } richiesti`
        );
      }

      if (competitionType.max_participants && config.participants > competitionType.max_participants) {
        warnings.push(
          `Massimo consigliato: ${competitionType.max_participants} partecipanti`
        );
      }
    }

    // Validate scoring system
    if (config.scoringSystem) {
      if (!competitionType.scoring_systems.includes(config.scoringSystem)) {
        errors.push(
          `Sistema di punteggio "${config.scoringSystem}" non supportato. Opzioni: ${competitionType.scoring_systems.join(', ')}`
        );
      }
    }

    // Validate boards per round (Bridge-specific)
    if (config.boardsPerRound !== undefined && competitionType.boards_per_round) {
      if (config.boardsPerRound < 1) {
        errors.push('Il numero di smazzate per turno deve essere almeno 1');
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Get scoring system label from cached plugin data (SINGLE SOURCE OF TRUTH)
   * Falls back to code if not found in cache
   */
  getScoringSystemLabel(code: string): string {
    // Search in all cached disciplines for the scoring system
    for (const discipline of this.disciplinesCache.values()) {
      if (discipline.scoring_systems) {
        const system = discipline.scoring_systems.find(ss => ss.code === code);
        if (system) {
          return system.name;
        }
      }
    }
    // Fallback to code if not found
    return code;
  }

  /**
   * Get participant type label in Italian
   */
  getParticipantTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      individual: 'Individuale',
      pair: 'Coppie',
      team: 'Squadre'
    };
    return labels[type] || type;
  }

  /**
   * Clear cache (useful for testing or forced refresh)
   */
  clearCache(): void {
    this.disciplinesCache.clear();
    this.competitionTypesCache.clear();
    this.lastFetch = 0;
  }
}

// Singleton instance
export const competitionPluginService = new CompetitionPluginService();

export default competitionPluginService;
